import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submissionStatusLabel } from '../../../constants/submissionStatus';
import { useToast } from '../../../components/ui/Toast';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import submissionService from '../../../services/submissionService';

const SubmissionDetailCard = ({ submission, getStatusBadgeColor, formatDate, onDelete, deleting = false }) => {
  // Replaces toastError(): non-blocking and styled; errors persist until
  // dismissed rather than vanishing on a timer.
  const { error: toastError } = useToast();
  const { t: tc } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);

  const handleOpenFile = async (fileUrl) => {
    if (!fileUrl) return;
    
    try {
      const urlData = await submissionService?.getSubmissionFileUrl(fileUrl);
      if (urlData?.url) {
        window.open(urlData?.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error opening file:', error);
      toastError(tc('files.openFileFailed'));
    }
  };

  const handleDownloadFile = async (fileUrl, fileName) => {
    if (!fileUrl) return;
    
    try {
      const urlData = await submissionService?.getSubmissionFileUrl(fileUrl);
      if (urlData?.url) {
        const link = document.createElement('a');
        link.href = urlData?.url;
        link.download = fileName || 'download';
        link.target = '_blank';
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toastError(tc('files.downloadFailed'));
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Header Section */}
      <div className="bg-muted/40 p-4 md:p-6 border-b border-border">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
              {submission?.title}
            </h3>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Icon name="Calendar" size={14} />
                <span>Submitted: {formatDate(submission?.submitted_at)}</span>
              </div>
              {submission?.track && (
                <div className="flex items-center gap-1.5">
                  <Icon name="Tag" size={14} />
                  <span>{submission?.track}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap ${getStatusBadgeColor(submission?.status)}`}>
              {submissionStatusLabel(submission?.status, tc)}
            </span>
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(submission?.id)}
                loading={deleting}
                disabled={deleting}
                iconName="Trash2"
                className="text-muted-foreground hover:text-error hover:bg-error/10"
                title="Delete submission"
                aria-label={`Delete submission ${submission?.title || ''}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4 md:p-6">
        {/* Abstract Preview */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
            <Icon name="FileText" size={14} />
            Abstract
          </h4>
          <p className={`text-sm text-muted-foreground leading-relaxed ${
            expanded ? '' : 'line-clamp-3'
          }`}>
            {submission?.abstract || 'No abstract provided'}
          </p>
          {submission?.abstract && submission?.abstract?.length > 200 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-primary hover:underline mt-2 flex items-center gap-1"
            >
              {expanded ? (
                <>
                  <span>Show less</span>
                  <Icon name="ChevronUp" size={14} />
                </>
              ) : (
                <>
                  <span>Show more</span>
                  <Icon name="ChevronDown" size={14} />
                </>
              )}
            </button>
          )}
        </div>

        {/* Keywords */}
        {submission?.keywords && submission?.keywords?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <Icon name="Hash" size={14} />
              Keywords
            </h4>
            <div className="flex flex-wrap gap-2">
              {submission?.keywords?.map((keyword, index) => (
                <span
                  key={index}
                  className="px-2.5 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Co-Authors */}
        {submission?.coAuthors && submission?.coAuthors?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <Icon name="Users" size={14} />
              Co-Authors
            </h4>
            <div className="space-y-2">
              {submission?.coAuthors?.map((author, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="User" size={12} className="flex-shrink-0" />
                  <span>{author?.name}</span>
                  {author?.email && (
                    <>
                      <span className="text-border">•</span>
                      <span className="text-xs">{author?.email}</span>
                    </>
                  )}
                  {author?.affiliation && (
                    <>
                      <span className="text-border">•</span>
                      <span className="text-xs">{author?.affiliation}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Files Section */}
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="Paperclip" size={14} />
            Attached Files
          </h4>
          
          <div className="space-y-2">
            {/* Main submission file */}
            {submission?.pdfUrl && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={20} className="text-error" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {submission?.pdfFileName || 'Submission file'}
                    </p>
                    <p className="text-xs text-muted-foreground">Submission Document</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenFile(submission?.pdfUrl)}
                    iconName="Eye"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Open
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownloadFile(submission?.pdfUrl, submission?.pdfFileName)}
                    iconName="Download"
                    iconSize={14}
                  />
                </div>
              </div>
            )}

            {/* Supplementary Files */}
            {submission?.supplementaryFiles && submission?.supplementaryFiles?.length > 0 && (
              submission?.supplementaryFiles?.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="File" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {file?.name || `Supplementary File ${index + 1}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {file?.type || 'Document'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenFile(file?.url)}
                      iconName="Eye"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Open
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadFile(file?.url, file?.name)}
                      iconName="Download"
                      iconSize={14}
                    />
                  </div>
                </div>
              ))
            )}

            {/* No Files State */}
            {!submission?.pdfUrl && (!submission?.supplementaryFiles || submission?.supplementaryFiles?.length === 0) && (
              <div className="text-center py-4 text-sm text-muted-foreground">
                <Icon name="FileX" size={24} className="mx-auto mb-2 opacity-50" />
                <p>No files attached</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetailCard;
